import React, { useState, useEffect } from 'react'
import { X, TestTube, Upload, Play, Eye, Download, AlertCircle, CheckCircle, Clock } from 'lucide-react'
import { adminApi } from '../services/api'
import { WorkflowVersion, PlaceholderInfo } from '../types'
import toast from 'react-hot-toast'

interface WorkflowTesterProps {
  isOpen: boolean
  onClose: () => void
  workflow?: WorkflowVersion | null
}

export default function WorkflowTester({ isOpen, onClose, workflow }: WorkflowTesterProps) {
  const [testImage, setTestImage] = useState<File | null>(null)
  const [testImagePreview, setTestImagePreview] = useState<string | null>(null)
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({})
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<any>(null)
  const [workflowDetails, setWorkflowDetails] = useState<any>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)

  useEffect(() => {
    if (isOpen && workflow) {
      loadWorkflowDetails()
      resetTestState()
    }
  }, [isOpen, workflow])

  const loadWorkflowDetails = async () => {
    if (!workflow) return

    try {
      setLoadingDetails(true)
      const response = await adminApi.getWorkflowDetails(workflow.id)
      if (response.success) {
        setWorkflowDetails(response.data)
        extractPlaceholders(response.data.workflow_json)
      }
    } catch (error) {
      console.error('Error loading workflow details:', error)
      toast.error('Failed to load workflow details')
    } finally {
      setLoadingDetails(false)
    }
  }

  const extractPlaceholders = (workflowJson: Record<string, any>) => {
    const placeholderPattern = /\{([^}]+)\}/g
    const foundPlaceholders = new Set<string>()
    
    const searchObject = (obj: any) => {
      if (typeof obj === 'string') {
        let match
        while ((match = placeholderPattern.exec(obj)) !== null) {
          foundPlaceholders.add(match[1])
        }
      } else if (typeof obj === 'object' && obj !== null) {
        Object.values(obj).forEach(searchObject)
      }
    }

    searchObject(workflowJson)
    
    // Initialize placeholder values with defaults
    const initialPlaceholders: Record<string, string> = {}
    foundPlaceholders.forEach(placeholder => {
      switch (placeholder) {
        case 'style':
          initialPlaceholders[placeholder] = 'modern'
          break
        case 'room_type':
          initialPlaceholders[placeholder] = 'living room'
          break
        case 'quality':
          initialPlaceholders[placeholder] = workflow?.quality_tier || 'medium'
          break
        case 'positive_prompt':
          initialPlaceholders[placeholder] = 'beautiful, well-lit, professional interior design'
          break
        case 'negative_prompt':
          initialPlaceholders[placeholder] = 'blurry, dark, cluttered, unprofessional'
          break
        default:
          initialPlaceholders[placeholder] = ''
      }
    })
    
    setPlaceholders(initialPlaceholders)
  }

  const resetTestState = () => {
    setTestImage(null)
    setTestImagePreview(null)
    setTestResult(null)
    setPlaceholders({})
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Image file must be less than 10MB')
        return
      }

      setTestImage(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setTestImagePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handlePlaceholderChange = (key: string, value: string) => {
    setPlaceholders(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const runTest = async () => {
    if (!workflow) {
      toast.error('No workflow selected')
      return
    }

    // Validate required placeholders
    const missingPlaceholders = Object.entries(placeholders)
      .filter(([key, value]) => !value.trim())
      .map(([key]) => key)

    if (missingPlaceholders.length > 0) {
      toast.error(`Please fill in all placeholders: ${missingPlaceholders.join(', ')}`)
      return
    }

    try {
      setTesting(true)
      setTestResult(null)

      const response = await adminApi.testWorkflow(workflow.id, testImage || undefined)
      
      if (response.success) {
        setTestResult(response.data)
        toast.success('Test completed successfully')
      } else {
        toast.error('Test failed: ' + (response.message || 'Unknown error'))
        setTestResult({ success: false, error: response.message || 'Test failed' })
      }
    } catch (error: any) {
      console.error('Test error:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Test failed'
      toast.error(errorMessage)
      setTestResult({ success: false, error: errorMessage })
    } finally {
      setTesting(false)
    }
  }

  const handleClose = () => {
    if (!testing) {
      resetTestState()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Workflow Tester</h3>
                {workflow && (
                  <p className="text-sm text-gray-600">
                    Testing: {workflow.name} (v{workflow.version})
                  </p>
                )}
              </div>
              <button
                onClick={handleClose}
                disabled={testing}
                className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {loadingDetails ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600">Loading workflow details...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Placeholders Section */}
                {Object.keys(placeholders).length > 0 && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Workflow Placeholders</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(placeholders).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => handlePlaceholderChange(key, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                            placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                            disabled={testing}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Test Image Upload */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Test Image (Optional)</h4>
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={testing}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Upload a test image to use for generation (max 10MB)
                      </p>
                    </div>
                    {testImagePreview && (
                      <div className="w-24 h-24 border border-gray-300 rounded-lg overflow-hidden">
                        <img
                          src={testImagePreview}
                          alt="Test preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Test Button */}
                <div className="flex justify-center">
                  <button
                    onClick={runTest}
                    disabled={testing || Object.keys(placeholders).length === 0}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {testing ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Running Test...
                      </>
                    ) : (
                      <>
                        <TestTube className="h-5 w-5 mr-2" />
                        Run Test Generation
                      </>
                    )}
                  </button>
                </div>

                {/* Test Results */}
                {testResult && (
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">Test Results</h4>
                    
                    {testResult.success ? (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2 text-green-700">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-medium">Test Successful</span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Processing Time:</span>
                            <span className="ml-2 font-medium">{testResult.processing_time}s</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Service:</span>
                            <span className="ml-2 font-medium">{testResult.service}</span>
                          </div>
                          {testResult.credits_used && (
                            <div>
                              <span className="text-gray-600">Credits Used:</span>
                              <span className="ml-2 font-medium">{testResult.credits_used}</span>
                            </div>
                          )}
                        </div>

                        {testResult.image_url && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Generated Image:</h5>
                            <div className="border border-gray-300 rounded-lg overflow-hidden max-w-md">
                              <img
                                src={testResult.image_url}
                                alt="Generated result"
                                className="w-full h-auto"
                              />
                            </div>
                            <a
                              href={testResult.image_url}
                              download="test-result.png"
                              className="inline-flex items-center mt-2 text-sm text-primary-600 hover:text-primary-700"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Download Image
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-red-700">
                          <AlertCircle className="h-5 w-5" />
                          <span className="font-medium">Test Failed</span>
                        </div>
                        {testResult.error && (
                          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                            {testResult.error}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
              <button
                onClick={handleClose}
                disabled={testing}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}